import { useEffect, useRef, useState } from "react";
import { ExerciseStrengthChart } from "./WorkoutsScreen";
import SideQuestHeatmap from "./SideQuestHeatmap";
import {
  LineChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import HabitHeatmap from "./HabitHeatmap";
import {
  getBodyStats,
  saveBodyStats,
  getWeightLog,
  addWeightEntry,
  deleteWeightEntry,
  getMeasurementsLog,
  addMeasurementsEntry,
  deleteMeasurementsEntry,
  getStepsLog,
  upsertStepsEntry,
  deleteStepsEntry,
  getPhotoLog,
  uploadProgressPhoto,
  savePhotoEntry,
  deletePhotoEntry,
} from "./bodyMetrics";
import { db } from "./firebase";
import { addDoc, collection, doc, getDocs, increment, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import {
  isAndroidNative,
  isIosNative,
  isNativeMobile,
  getHealthConnectAvailability,
  requestStepsPermission,
  readTodayStepsTotal,
  readStepsForDate,
} from "./healthConnectSteps";
import {
  getHealthSyncPrefs,
  saveHealthSyncPrefs,
  requestWorkoutsPermission,
  readTodayWorkouts,
  syncWorkoutsToCardioLog,
} from "./healthSync";
import { getActivePromptForUser, getUserResponseForPrompt } from "./weeklyQuests";

const WEIGHT_RANGES = [
  { label: "1M", months: 1 },
  { label: "3M", months: 3 },
  { label: "6M", months: 6 },
  { label: "1Y", months: 12 },
  { label: "2Y", months: 24 },
];

const MEASUREMENT_KEYS = ["chest", "shoulders", "waist", "hip"];
const ANGLES = ["front", "side", "back"];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function ageFromBirthday(birthday) {
  if (!birthday) return null;
  const birth = new Date(birthday + "T00:00:00");
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}

function filterByMonths(entries, months) {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  const cutoffStr = cutoff.toISOString().slice(0, 10);
  return entries.filter((e) => e.date >= cutoffStr);
}

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split("-");
  return `${m}/${d}/${y.slice(2)}`;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const card = {
  background: "#f5faff",
  borderRadius: "22px",
  padding: "20px",
  border: "2px solid #dbeafe",
  boxShadow: "0 8px 20px rgba(30, 58, 138, 0.10)",
  marginBottom: "16px",
};

const inputStyle = {
  padding: "10px 14px",
  borderRadius: "12px",
  border: "2px solid #bfdbfe",
  background: "#f8fbff",
  color: "#1e3a5f",
  fontSize: "14px",
  width: "100%",
  boxSizing: "border-box",
};

const btnPrimary = {
  padding: "10px 18px",
  borderRadius: "12px",
  border: "2px solid #2563eb",
  background: "#e0f2fe",
  color: "#1e3a5f",
  fontWeight: "bold",
  cursor: "pointer",
  fontSize: "14px",
  whiteSpace: "nowrap",
};

const btnGhost = {
  padding: "6px 12px",
  borderRadius: "10px",
  border: "2px solid #bfdbfe",
  background: "#f5faff",
  color: "#4b6b8a",
  cursor: "pointer",
  fontSize: "13px",
};

const sectionTitle = {
  fontWeight: "bold",
  color: "#1e3a5f",
  fontSize: "16px",
};

const labelStyle = {
  fontWeight: "bold",
  color: "#1e3a5f",
  fontSize: "13px",
  marginBottom: "6px",
};

const logRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 12px",
  borderRadius: "12px",
  background: "#f8fbff",
  border: "1px solid #bfdbfe",
  fontSize: "13px",
  color: "#1e3a5f",
};

// ── Component ─────────────────────────────────────────────────────────────────

// Default order of stats sections
const DEFAULT_STATS_ORDER = ["heatmap", "steps", "sideQuests", "aboutYou", "weight", "measurements", "stepsLog", "strength"];

export default function YouScreen({ currentUser, profileData, defaultSubTab, hideNav, dailySelections, revivedDays, customHabits, gameContent, onVideoGenderChange, isMomentum, sideQuestCompletions, activeSideQuests, onReplayOnboarding, categoryLabels, bodyMetricsConfig, bodyMetricsXpLog, onBodyMetricXpAwarded, onNavigateToSettings, showRecap = false, initAction, onInitActionConsumed }) {
  const uid = currentUser?.uid;
  const [subTab, setSubTab] = useState(defaultSubTab || "profile");

  // Recap tab — active assignment/response
  const [recapPrompt, setRecapPrompt] = useState(undefined); // undefined=not loaded, null=none
  const [recapResponse, setRecapResponse] = useState(null);
  const [recapLoaded, setRecapLoaded] = useState(false);

  // Badge showcase — pinned badges (stored in bodyStats)
  const [pinnedBadges, setPinnedBadges] = useState([]);
  const [badgePinMode, setBadgePinMode] = useState(false);
  const [loading, setLoading] = useState(true);

  // About You
  const [stats, setStats] = useState({});
  const [editingProfile, setEditingProfile] = useState(false);
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [birthday, setBirthday] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);

  // Video demo gender preference
  const [videoGender, setVideoGender] = useState("women");
  const [videoGenderSaving, setVideoGenderSaving] = useState(false);

  // Stats section order (drag-to-reorder)
  const [statsOrder, setStatsOrder] = useState(DEFAULT_STATS_ORDER);
  const [reorderMode, setReorderMode] = useState(false);
  const [draggingIdx, setDraggingIdx] = useState(null);
  const longPressTimer = useRef(null);
  const dragStartY = useRef(null);
  const dragCurrentY = useRef(null);

  // Weight
  const [weightLog, setWeightLog] = useState([]);
  const [weightRange, setWeightRange] = useState(3);
  const [addingWeight, setAddingWeight] = useState(false);
  const [weightExpanded, setWeightExpanded] = useState(false);
  const [weightInput, setWeightInput] = useState("");
  const [weightDate, setWeightDate] = useState(todayStr());
  const [weightUnit, setWeightUnit] = useState("lbs");
  const [weightSaving, setWeightSaving] = useState(false);
  const [weightError, setWeightError] = useState("");

  // Measurements
  const [measurementsLog, setMeasurementsLog] = useState([]);
  const [addingMeasurements, setAddingMeasurements] = useState(false);
  const [measurementsExpanded, setMeasurementsExpanded] = useState(false);
  const [measureInputs, setMeasureInputs] = useState({ chest: "", shoulders: "", waist: "", hip: "" });
  const [measureDate, setMeasureDate] = useState(todayStr());
  const [measureSaving, setMeasureSaving] = useState(false);
  const [measureError, setMeasureError] = useState("");

  // Heatmap range — default 3 months
  const [heatmapWeeks, setHeatmapWeeks] = useState(13);

  // Manual step goal override (used when main quest habit has no step count)
  const [manualStepGoal, setManualStepGoal] = useState(null);
  const [editingStepGoal, setEditingStepGoal] = useState(false);
  const [stepGoalDraft, setStepGoalDraft] = useState("");

  // Steps
  const [stepsLog, setStepsLog] = useState([]);
  const [addingSteps, setAddingSteps] = useState(false);
  const [stepsExpanded, setStepsExpanded] = useState(false);
  const [stepsInput, setStepsInput] = useState("");
  const [stepsDate, setStepsDate] = useState(todayStr());
  const [stepsSaving, setStepsSaving] = useState(false);
  const [stepsError, setStepsError] = useState("");
  const [healthConnectStatus, setHealthConnectStatus] = useState("Not connected");
  const [healthConnectAvailable, setHealthConnectAvailable] = useState(false);
  const [healthSyncing, setHealthSyncing] = useState(false);
  const [showFitbitHelp, setShowFitbitHelp] = useState(false);
  const [healthSyncPrefs, setHealthSyncPrefs] = useState({ workouts: false, autoSideQuests: false });
  const [healthSyncStatus, setHealthSyncStatus] = useState("");
  const [healthSyncLoading, setHealthSyncLoading] = useState(false);

  // Photos
  const [photoLog, setPhotoLog] = useState([]);
  const [photoDate, setPhotoDate] = useState(todayStr());
  const [uploading, setUploading] = useState({});
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [compareAngle, setCompareAngle] = useState("front");
  const fileRefs = { front: useRef(), side: useRef(), back: useRef() };
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { date, angle } or null
  const [photoError, setPhotoError] = useState("");
  const [deleteWeightConfirm, setDeleteWeightConfirm] = useState(null); // entry id or null
  const [deleteMeasureConfirm, setDeleteMeasureConfirm] = useState(null); // entry id or null

  // XP adjustments log (loaded from Firestore on demand)
  const [xpAdjustments, setXpAdjustments] = useState(null); // null = not loaded yet
  const [xpAdjustmentsLoading, setXpAdjustmentsLoading] = useState(false);

  useEffect(() => {
    if (!uid) return;
    loadAll();
  }, [uid]);

  async function loadAll() {
    setLoading(true);
    try {
      const [s, w, m, st, p, hPrefs] = await Promise.all([
        getBodyStats(uid),
        getWeightLog(uid),
        getMeasurementsLog(uid),
        getStepsLog(uid),
        getPhotoLog(uid),
        getHealthSyncPrefs(uid),
      ]);
      setHealthSyncPrefs(hPrefs);
      setStats(s);
      setHeightFt(s.heightFt != null ? String(s.heightFt) : "");
      setHeightIn(s.heightIn != null ? String(s.heightIn) : "");
      setBirthday(s.birthday ?? "");
      if (s.videoGender) setVideoGender(s.videoGender);
      if (Array.isArray(s.statsOrder) && s.statsOrder.length > 0) setStatsOrder(s.statsOrder);
      if (Array.isArray(s.pinnedBadges)) setPinnedBadges(s.pinnedBadges);
      setWeightLog(w);
      setMeasurementsLog(m);
      setStepsLog(st);
      setPhotoLog(p);
    } finally {
      setLoading(false);
    }
    autoSyncSteps(uid);
  }

  // ── Profile ────────────────────────────────────────────────────────────────

  async function handleSaveProfile() {
    setProfileSaving(true);
    try {
      await saveBodyStats(uid, {
        heightFt: heightFt !== "" ? Number(heightFt) : null,
        heightIn: heightIn !== "" ? Number(heightIn) : null,
        birthday: birthday || null,
      });
      const s = await getBodyStats(uid);
      setStats(s);
      setEditingProfile(false);
    } finally {
      setProfileSaving(false);
    }
  }

  const age = ageFromBirthday(stats.birthday);
  const hasProfile = stats.heightFt != null || stats.birthday;

  // ── Body Metrics XP helpers ───────────────────────────────────────────────

  // Returns true if today is a scheduled due-day for the metric (based on day-of-week + interval)
  function isMetricDueToday(metricKey) {
    const cfg = bodyMetricsConfig?.[metricKey];
    if (!cfg?.enabled || !cfg?.scheduleDays?.length) return false;
    const today = new Date();
    const dayOfWeek = today.getDay();
    if (!cfg.scheduleDays.includes(dayOfWeek)) return false;
    if ((cfg.intervalWeeks || 1) > 1) {
      // Use epoch week number to decide if this is an "active" week
      const weekNum = Math.floor(today.getTime() / (7 * 24 * 60 * 60 * 1000));
      if (weekNum % cfg.intervalWeeks !== 0) return false;
    }
    return true;
  }

  // Returns XP amount for metric if enabled and not yet awarded for dateStr, else 0
  function getMetricXpForDate(metricKey, dateStr) {
    const cfg = bodyMetricsConfig?.[metricKey];
    if (!cfg?.enabled || !cfg?.xp) return 0;
    const logKey = `${metricKey}_${dateStr}`;
    if (bodyMetricsXpLog?.[logKey]) return 0; // already awarded
    return cfg.xp;
  }

  // Awards XP for logging a metric on a date (idempotent — checks log first)
  async function awardMetricXp(metricKey, dateStr) {
    const xpAmount = getMetricXpForDate(metricKey, dateStr);
    if (!xpAmount) return;
    await updateDoc(doc(db, "users", uid), {
      xpAdjustmentTotal: increment(xpAmount),
      [`bodyMetricsXpLog.${metricKey}_${dateStr}`]: xpAmount,
    });
    const labelMap = { weight: "Weight logged", measurements: "Measurements logged", photos: "Progress photo uploaded" };
    await addDoc(collection(db, "users", uid, "xpAdjustments"), {
      amount: xpAmount,
      type: "earn",
      source: "body_metric",
      note: `${labelMap[metricKey] || metricKey} (${dateStr})`,
      createdAt: serverTimestamp(),
    });
    if (onBodyMetricXpAwarded) onBodyMetricXpAwarded(metricKey, dateStr, xpAmount);
  }

  // ── Weight ────────────────────────────────────────────────────────────────

  async function handleAddWeight() {
    if (!weightInput || !weightDate) return;
    setWeightSaving(true);
    setWeightError("");
    try {
      await addWeightEntry(uid, { date: weightDate, weight: weightInput, unit: weightUnit });
      await awardMetricXp("weight", weightDate);
      const w = await getWeightLog(uid);
      setWeightLog(w);
      setWeightInput("");
      setWeightDate(todayStr());
      setAddingWeight(false);
    } catch {
      setWeightError("Couldn't save weight entry. Please try again.");
    } finally {
      setWeightSaving(false);
    }
  }

  async function handleDeleteWeight(id) {
    await deleteWeightEntry(uid, id);
    setWeightLog(await getWeightLog(uid));
  }

  const filteredWeight = filterByMonths(weightLog, weightRange);
  const chartData = filteredWeight.map((e) => ({
    date: formatDate(e.date),
    weight: e.weight,
    unit: e.unit,
  }));
  const latestWeight = weightLog.length > 0 ? weightLog[weightLog.length - 1] : null;

  // ── Measurements ──────────────────────────────────────────────────────────

  async function handleAddMeasurements() {
    const hasAny = MEASUREMENT_KEYS.some((k) => measureInputs[k] !== "");
    if (!hasAny || !measureDate) return;
    setMeasureSaving(true);
    setMeasureError("");
    try {
      await addMeasurementsEntry(uid, { date: measureDate, ...measureInputs });
      await awardMetricXp("measurements", measureDate);
      const m = await getMeasurementsLog(uid);
      setMeasurementsLog(m);
      setMeasureInputs({ chest: "", shoulders: "", waist: "", hip: "" });
      setMeasureDate(todayStr());
      setAddingMeasurements(false);
    } catch {
      setMeasureError("Couldn't save measurements. Please try again.");
    } finally {
      setMeasureSaving(false);
    }
  }

  async function handleDeleteMeasurements(id) {
    await deleteMeasurementsEntry(uid, id);
    setMeasurementsLog(await getMeasurementsLog(uid));
  }

  const latestMeasurements = measurementsLog.length > 0 ? measurementsLog[0] : null;

  // ── Steps chart data ───────────────────────────────────────────────────────
  const [stepsRange, setStepsRange] = useState(3);
  const STEPS_RANGES = [
    { label: "1M", months: 1 },
    { label: "3M", months: 3 },
    { label: "6M", months: 6 },
    { label: "1Y", months: 12 },
  ];

  const filteredSteps = filterByMonths(stepsLog, stepsRange);
  const stepsChartData = filteredSteps.map((e) => ({
    date: formatDate(e.date),
    steps: Number(e.steps),
  }));

  // Detect step goal from the MAIN QUEST movement habit name.
  // Handles: 7000, 7,000, 7k, 7.5k, 10K, 10,000 steps, etc.
  function extractStepGoal(habits) {
    if (!habits) return null;
    const name = habits.movement?.main?.habitName || "";
    if (!name) return null;

    // Match patterns like: 7k, 7.5k, 7K, 7,500, 7000
    const kMatch = name.match(/(\d+(?:\.\d+)?)\s*k/i);
    if (kMatch) {
      const num = Math.round(parseFloat(kMatch[1]) * 1000);
      if (num >= 500 && num <= 200000) return num;
    }

    // Match plain numbers (with optional commas): 7000, 7,000, 10000
    const plainMatch = name.match(/\b(\d{1,3}(?:,\d{3}|\d)*)\b/);
    if (plainMatch) {
      const num = parseInt(plainMatch[1].replace(/,/g, ""), 10);
      if (num >= 500 && num <= 200000) return num;
    }

    return null;
  }

  // Use habit-derived goal if available, otherwise fall back to manual override
  const habitStepGoal = extractStepGoal(customHabits);
  const stepGoal = habitStepGoal ?? manualStepGoal;

  // ── Steps ─────────────────────────────────────────────────────────────────

  async function handleAddSteps() {
    if (!stepsInput || !stepsDate) return;
    setStepsSaving(true);
    setStepsError("");
    try {
      await upsertStepsEntry(uid, {
        date: stepsDate,
        steps: stepsInput,
        source: "manual",
      });
      const st = await getStepsLog(uid);
      setStepsLog(st);
      setStepsInput("");
      setStepsDate(todayStr());
      setAddingSteps(false);
    } catch {
      setStepsError("Couldn't save steps. Please try again.");
    } finally {
      setStepsSaving(false);
    }
  }

  async function handleDeleteSteps(id) {
    await deleteStepsEntry(uid, id);
    setStepsLog(await getStepsLog(uid));
  }

  async function refreshHealthConnectAvailability() {
    if (!isAndroidNative() && !isIosNative()) {
      setHealthConnectAvailable(false);
      setHealthConnectStatus("Step sync is available on Android and iOS app builds only.");
      return;
    }

    const availability = await getHealthConnectAvailability();
    setHealthConnectAvailable(Boolean(availability.available));
    setHealthConnectStatus(
      availability.available
        ? isIosNative() ? "Apple HealthKit ready" : "Health Connect ready"
        : availability.reason || "Health data not available"
    );
  }

  useEffect(() => {
    refreshHealthConnectAvailability();
  }, []);

  // Open the relevant logging form immediately when launched from a FAB shortcut
  useEffect(() => {
    if (!initAction) return;
    if (initAction === "weight") {
      setSubTab("stats");
      setAddingWeight(true);
      setTimeout(() => document.getElementById("you-weight-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      setTimeout(() => document.querySelector("#you-weight-section input[type='number']")?.focus(), 350);
    } else if (initAction === "measurements") {
      setSubTab("stats");
      setAddingMeasurements(true);
      setTimeout(() => document.getElementById("you-measurements-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      setTimeout(() => document.querySelector("#you-measurements-section input[type='number']")?.focus(), 350);
    } else if (initAction === "photos") {
      setSubTab("photos");
    }
    onInitActionConsumed?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (subTab !== "recap" || recapLoaded || !uid) return;
    setRecapLoaded(true);
    const tag = currentUser?.programTag || "";
    getActivePromptForUser(tag).then(async (prompt) => {
      setRecapPrompt(prompt || null);
      if (prompt) {
        const resp = await getUserResponseForPrompt(prompt.id, uid).catch(() => null);
        setRecapResponse(resp || null);
      }
    }).catch(() => setRecapPrompt(null));
  }, [subTab, recapLoaded, uid, currentUser?.programTag]);

  async function autoSyncSteps(uidArg) {
    if (!uidArg || (!isAndroidNative() && !isIosNative())) return;
    try {
      const availability = await getHealthConnectAvailability();
      if (!availability.available) return;
      const permission = await requestStepsPermission();
      if (!permission.granted) return;

      const today = todayStr();
      const yesterdayDate = new Date();
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);
      const yesterday = yesterdayDate.toISOString().slice(0, 10);

      const [todayResult, yesterdayResult] = await Promise.all([
        readTodayStepsTotal(),
        readStepsForDate(yesterday),
      ]);

      if (todayResult.ok) {
        await upsertStepsEntry(uidArg, { date: today, steps: todayResult.steps, source: "health-connect" });
      }
      if (yesterdayResult.ok && yesterdayResult.steps > 0) {
        await upsertStepsEntry(uidArg, { date: yesterday, steps: yesterdayResult.steps, source: "health-connect" });
      }

      const updated = await getStepsLog(uidArg);
      setStepsLog(updated);
    } catch {
      // Silent — auto-sync failures don't surface to the user
    }
  }

  // Listen for app coming back to foreground and re-sync steps
  useEffect(() => {
    if (!uid || (!isAndroidNative() && !isIosNative())) return;
    function onVisibilityChange() {
      if (document.visibilityState === "visible") autoSyncSteps(uid);
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [uid]);

  async function handleConnectAndSyncSteps() {
    if (!uid) return;
    setHealthSyncing(true);

    try {
      const availability = await getHealthConnectAvailability();
      setHealthConnectAvailable(Boolean(availability.available));

      if (!availability.available) {
        setHealthConnectStatus(
          availability.reason || "Health Connect is not available on this device"
        );
        return;
      }

      const permission = await requestStepsPermission();
      if (!permission.granted) {
        setHealthConnectStatus("Permission denied for step data.");
        return;
      }

      const result = await readTodayStepsTotal();
      if (!result.ok) {
        setHealthConnectStatus(result.reason || "Could not read steps from Health Connect.");
        return;
      }

      const today = todayStr();
      await upsertStepsEntry(uid, {
        date: today,
        steps: result.steps,
        source: "health-connect",
      });

      const updated = await getStepsLog(uid);
      setStepsLog(updated);
      setHealthConnectStatus(`Synced ${Number(result.steps).toLocaleString()} steps for today.`);
    } catch (error) {
      setHealthConnectStatus(error?.message || "Health Connect sync failed.");
    } finally {
      setHealthSyncing(false);
    }
  }

  async function handleToggleHealthSyncPref(key, value) {
    const updated = { ...healthSyncPrefs, [key]: value };
    setHealthSyncPrefs(updated);
    await saveHealthSyncPrefs(uid, updated);
  }

  async function handleSyncWorkoutsNow() {
    if (!isAndroidNative() && !isIosNative()) return;
    setHealthSyncLoading(true);
    setHealthSyncStatus("");
    try {
      const permResult = await requestWorkoutsPermission();
      if (!permResult.granted) {
        setHealthSyncStatus("Permission denied for workout data.");
        return;
      }
      const workouts = await readTodayWorkouts();
      if (workouts.length === 0) {
        setHealthSyncStatus("No workouts recorded today.");
        return;
      }
      const added = await syncWorkoutsToCardioLog(uid, workouts);
      if (added.length === 0) {
        setHealthSyncStatus(`${workouts.length} workout${workouts.length !== 1 ? "s" : ""} already synced.`);
      } else {
        setHealthSyncStatus(`Synced ${added.length} workout${added.length !== 1 ? "s" : ""} to your cardio log.`);
      }
    } catch (err) {
      setHealthSyncStatus(err?.message || "Sync failed.");
    } finally {
      setHealthSyncLoading(false);
    }
  }

  // ── Photos ────────────────────────────────────────────────────────────────

  async function handlePhotoUpload(angle, file) {
    if (!file || !photoDate) return;
    setUploading((prev) => ({ ...prev, [angle]: true }));
    setPhotoError("");
    try {
      const url = await uploadProgressPhoto(uid, photoDate, angle, file);
      await savePhotoEntry(uid, { date: photoDate, [angle]: url });
      // Award XP once per date (first upload for that date earns it)
      await awardMetricXp("photos", photoDate);
      setPhotoLog(await getPhotoLog(uid));
    } catch {
      setPhotoError("Couldn't upload photo. Please try again.");
    } finally {
      setUploading((prev) => ({ ...prev, [angle]: false }));
      // Reset file input so same file can be re-selected if needed
      if (fileRefs[angle].current) fileRefs[angle].current.value = "";
    }
  }

  async function handleDeletePhoto(date, angle) {
    await deletePhotoEntry(uid, date, angle);
    setPhotoLog(await getPhotoLog(uid));
  }

  const photoByDate = Object.fromEntries(photoLog.map((p) => [p.date, p]));
  const photoDates = [...photoLog.map((p) => p.date)].sort().reverse();
  const currentDatePhotos = photoByDate[photoDate] || {};

  if (loading) {
    return (
      <div style={{ padding: "40px", color: "#4b6b8a", textAlign: "center" }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "600px", width: "100%", boxSizing: "border-box", margin: "0 auto", padding: "0 16px 100px", overflowX: "hidden" }}>

      {/* Sub-tab nav */}
      {!hideNav && (
        <div style={{ display: "flex", gap: "10px", marginBottom: "20px", marginTop: "8px", flexWrap: "wrap" }}>
          {[["profile", "Profile"], ["stats", "Stats"], ["recap", "Recap"], ["xplog", "XP Log"], ["photos", "Photos"]].filter(([key]) => (key !== "profile" || !!profileData) && (key !== "recap" || showRecap)).map(([key, label]) => (
            <button
              key={key}
              onClick={() => {
                setSubTab(key);
                if (key === "xplog" && xpAdjustments === null && !xpAdjustmentsLoading) {
                  setXpAdjustmentsLoading(true);
                  getDocs(query(collection(db, "users", uid, "xpAdjustments"), orderBy("createdAt", "desc")))
                    .then((snap) => setXpAdjustments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
                    .catch(() => setXpAdjustments([]))
                    .finally(() => setXpAdjustmentsLoading(false));
                }
              }}
              style={{
                padding: "10px 20px",
                borderRadius: "14px",
                border: subTab === key ? "2px solid #2563eb" : "2px solid #bfdbfe",
                background: subTab === key ? "#e0f2fe" : "#f5faff",
                color: "#1e3a5f",
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ════════════════════ PROFILE TAB ════════════════════ */}
      {subTab === "profile" && profileData && (
        <>
          {/* Avatar + name + level */}
          <div style={{ ...card, textAlign: "center", padding: "24px" }}>
            {currentUser.avatar && (
              <img src={currentUser.avatar} alt={currentUser.name}
                onClick={onNavigateToSettings || undefined}
                style={{ width: "100px", height: "100px", borderRadius: "24px", objectFit: "cover", border: "3px solid #bfdbfe", marginBottom: "14px", boxShadow: "0 8px 20px rgba(121,94,148,0.12)", cursor: onNavigateToSettings ? "pointer" : "default" }} />
            )}
            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "26px" }}>{currentUser.name}</div>
            <div style={{ color: "#4b6b8a", fontSize: "15px", marginTop: "6px" }}>
              Level {profileData.level} · {profileData.totalXP} total XP
            </div>
            {/* XP progress bar */}
            <div style={{ marginTop: "14px", background: "#bfdbfe", borderRadius: "999px", height: "10px", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: "999px", background: "linear-gradient(90deg, #38bdf8, #2563eb)", width: `${(profileData.totalXP % 500) / 5}%` }} />
            </div>
            <div style={{ color: "#6b8cb0", fontSize: "12px", marginTop: "6px" }}>{profileData.totalXP % 500} / 500 XP to next level</div>
          </div>

          {/* Streak Shield status */}
          {profileData.streakShield !== undefined && (
            <div style={{
              marginTop: "16px",
              padding: "14px 18px",
              borderRadius: "18px",
              background: profileData.streakShield === "ready" ? "#fef9c3" : "#f5faff",
              border: profileData.streakShield === "ready" ? "2px solid #fde047" : "2px solid #dbeafe",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}>
              <span style={{ fontSize: "26px", flexShrink: 0 }}>🛡️</span>
              <div style={{ flex: 1 }}>
                {profileData.streakShield === "ready" ? (
                  <>
                    <div style={{ fontWeight: "bold", color: "#713f12", fontSize: "14px" }}>
                      Streak Shield ready
                    </div>
                    <div style={{ color: "#92400e", fontSize: "12px", marginTop: "2px" }}>
                      If you miss a full day, your shield will auto-activate and protect your streak.
                    </div>
                  </>
                ) : (() => {
                  // Calculate progress toward next shield
                  const startKey = profileData.shieldEarnProgressStart;
                  if (!startKey) {
                    return (
                      <>
                        <div style={{ fontWeight: "bold", color: "#4b6b8a", fontSize: "14px" }}>
                          No shield yet
                        </div>
                        <div style={{ color: "#6b8cb0", fontSize: "12px", marginTop: "2px" }}>
                          Complete 14 consecutive full quest days to earn your first shield.
                        </div>
                      </>
                    );
                  }
                  const startDate = new Date(`${startKey}T12:00:00`);
                  const todayKey = new Date().toISOString().slice(0, 10);
                  let count = 0;
                  for (let i = 0; i < 60; i++) {
                    const d = new Date(startDate);
                    d.setDate(startDate.getDate() + i);
                    const dk = d.toISOString().slice(0, 10);
                    if (dk > todayKey) break;
                    const sel = (profileData.dailySelections || {})[dk] || {};
                    const shielded = (profileData.shieldActivatedDays || {})[dk];
                    const full = (sel.nutrition && sel.movement && sel.mindset) || shielded;
                    if (full) { count++; } else { count = 0; }
                  }
                  const pct = Math.min(100, (count / 14) * 100);
                  const remaining = Math.max(0, 14 - count);
                  return (
                    <>
                      <div style={{ fontWeight: "bold", color: "#4b6b8a", fontSize: "14px" }}>
                        Earning next shield — {count}/14 days
                      </div>
                      <div style={{ color: "#6b8cb0", fontSize: "12px", marginTop: "2px" }}>
                        {remaining > 0 ? `${remaining} more full quest day${remaining === 1 ? "" : "s"} to go.` : "Almost there!"}
                      </div>
                      {/* Progress bar */}
                      <div style={{ marginTop: "8px", background: "#dbeafe", borderRadius: "999px", height: "6px", overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: "999px", background: "#2563eb", width: `${Math.min(100, (count / 14) * 100)}%`, transition: "width 0.3s" }} />
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Streaks */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "10px", marginTop: "16px" }}>
            {profileData.streaks.map(({ title, emoji, current, longest }) => (
              <div key={title} style={{
                background: "#f5faff", borderRadius: "16px", padding: "12px 14px",
                border: "2px solid #dbeafe", boxShadow: "0 4px 12px rgba(121,94,148,0.06)",
                display: "flex", alignItems: "center", gap: "10px", minWidth: 0,
              }}>
                <div style={{ fontSize: "22px", flexShrink: 0 }}>{emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "2px" }}>{title}</div>
                  <div style={{ display: "flex", gap: "10px", alignItems: "baseline" }}>
                    <span style={{ color: "#1e3a5f", fontSize: "20px", fontWeight: "bold" }}>{current}</span>
                    <span style={{ color: "#6b8cb0", fontSize: "11px" }}>now</span>
                    <span style={{ color: "#93c5fd", fontSize: "13px" }}>·</span>
                    <span style={{ color: "#4b6b8a", fontSize: "15px", fontWeight: "bold" }}>{longest}</span>
                    <span style={{ color: "#6b8cb0", fontSize: "11px" }}>best</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Badge Showcase — pin up to 3 favorites */}
          {profileData.badges.length > 0 && (
            <div style={{ ...card, marginTop: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px" }}>🏅 Badge Showcase</div>
                <button
                  type="button"
                  onClick={() => setBadgePinMode((p) => !p)}
                  style={{ padding: "5px 12px", borderRadius: "10px", border: "2px solid #bfdbfe", background: badgePinMode ? "#e0f2fe" : "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
                >
                  {badgePinMode ? "Done" : "✏️ Edit"}
                </button>
              </div>
              {badgePinMode ? (
                <>
                  <div style={{ color: "#6b8cb0", fontSize: "12px", marginBottom: "10px" }}>Tap a badge to pin/unpin it. You can pin up to 3.</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "8px" }}>
                    {profileData.badges.map((badge) => {
                      const isPinned = pinnedBadges.includes(badge.id || badge.name);
                      return (
                        <button
                          key={badge.id || badge.name}
                          type="button"
                          onClick={async () => {
                            const key = badge.id || badge.name;
                            let next;
                            if (isPinned) {
                              next = pinnedBadges.filter((k) => k !== key);
                            } else if (pinnedBadges.length < 3) {
                              next = [...pinnedBadges, key];
                            } else {
                              return; // already 3 pinned
                            }
                            setPinnedBadges(next);
                            await saveBodyStats(uid, { pinnedBadges: next });
                          }}
                          style={{ background: isPinned ? "#e0f2fe" : "#f8fbff", borderRadius: "14px", padding: "12px", border: isPinned ? "2px solid #2563eb" : "2px solid #bfdbfe", cursor: "pointer", textAlign: "left" }}
                        >
                          <div style={{ fontSize: "22px", marginBottom: "4px" }}>{badge.emoji}</div>
                          <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "12px" }}>{badge.name || badge.title}</div>
                          {isPinned && <div style={{ color: "#2563eb", fontSize: "10px", marginTop: "2px" }}>📌 Pinned</div>}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <>
                  {pinnedBadges.length > 0 ? (
                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      {pinnedBadges.map((key) => {
                        const badge = profileData.badges.find((b) => (b.id || b.name) === key);
                        if (!badge) return null;
                        return (
                          <div key={key} style={{ background: "#f0f9ff", borderRadius: "16px", padding: "14px 18px", border: "2px solid #7dd3fc", textAlign: "center", minWidth: "90px" }}>
                            <div style={{ fontSize: "32px", marginBottom: "6px" }}>{badge.emoji}</div>
                            <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "13px" }}>{badge.name || badge.title}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ color: "#6b8cb0", fontSize: "13px" }}>Tap Edit to pin your 3 favorite badges here.</div>
                  )}
                  {/* All badges below */}
                  <div style={{ marginTop: "14px", borderTop: "1px solid #dbeafe", paddingTop: "12px" }}>
                    <div style={{ color: "#4b6b8a", fontSize: "12px", fontWeight: "bold", marginBottom: "8px", textTransform: "uppercase" }}>All Earned ({profileData.badges.length})</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "8px" }}>
                      {profileData.badges.map((badge) => (
                        <div key={badge.id || badge.name} style={{ background: "#f8fbff", borderRadius: "14px", padding: "12px", border: "2px solid #bfdbfe" }}>
                          <div style={{ fontSize: "22px", marginBottom: "4px" }}>{badge.emoji}</div>
                          <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", marginBottom: "2px" }}>{badge.name || badge.title}</div>
                          <div style={{ color: "#4b6b8a", fontSize: "11px" }}>{badge.description || badge.subtitle}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {profileData.badges.length === 0 && (
            <div style={{ ...card, marginTop: "16px" }}>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px", marginBottom: "8px" }}>Badges Earned</div>
              <div style={{ color: "#4b6b8a", fontSize: "14px" }}>No badges yet — your first one is coming soon ✨</div>
            </div>
          )}

          {/* Group leaderboard (optional, admin controlled) */}
          {profileData.leaderboardVisible && (
            <div style={{ ...card, marginTop: "16px" }}>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px", marginBottom: "12px" }}>
                Group Leaderboard
              </div>

              {!Array.isArray(profileData.leaderboardEntries) || profileData.leaderboardEntries.length === 0 ? (
                <div style={{ color: "#4b6b8a", fontSize: "14px" }}>No leaderboard data yet.</div>
              ) : (
                <div style={{ display: "grid", gap: "8px" }}>
                  {profileData.leaderboardEntries.map((entry) => (
                    <div
                      key={entry.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 12px",
                        borderRadius: "12px",
                        border: entry.isMe ? "2px solid #2563eb" : "1px solid #bfdbfe",
                        background: entry.isMe ? "#e0f2fe" : "#f8fbff",
                      }}
                    >
                      <div style={{ width: "24px", textAlign: "center", color: "#4b6b8a", fontWeight: "bold", fontSize: "13px" }}>
                        #{entry.rank}
                      </div>

                      {entry.avatar ? (
                        <img
                          src={entry.avatar}
                          alt={entry.name}
                          style={{ width: "30px", height: "30px", borderRadius: "999px", objectFit: "cover", border: "2px solid #bfdbfe" }}
                        />
                      ) : (
                        <div style={{ width: "30px", height: "30px", borderRadius: "999px", background: "#bfdbfe" }} />
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "13px" }}>
                          {entry.name}
                          {entry.isMe ? " (You)" : ""}
                        </div>
                        <div style={{ color: "#4b6b8a", fontSize: "12px" }}>
                          Level {entry.level} · {entry.totalXP} XP
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ════════════════════ STATS TAB ════════════════════ */}
      {subTab === "stats" && (() => {
        // Build the ordered list of section keys, filtering out momentum-only sections for non-momentum users
        const allSectionKeys = DEFAULT_STATS_ORDER;
        const orderedKeys = [
          ...statsOrder.filter((k) => allSectionKeys.includes(k)),
          ...allSectionKeys.filter((k) => !statsOrder.includes(k)),
        ].filter((k) => k !== "strength" || isMomentum);

        function moveSection(fromIdx, toIdx) {
          if (toIdx < 0 || toIdx >= orderedKeys.length) return;
          const newOrder = [...orderedKeys];
          const [moved] = newOrder.splice(fromIdx, 1);
          newOrder.splice(toIdx, 0, moved);
          setStatsOrder(newOrder);
          // Persist to bodyStats
          saveBodyStats(uid, { statsOrder: newOrder }).catch(console.error);
        }

        function renderSection(key) {
          switch (key) {
            case "heatmap":
              return (
                <div key="heatmap">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <span style={sectionTitle}>🗓️ Habit Heatmap</span>
                    <div style={{ display: "flex", gap: "6px" }}>
                      {[{ label: "3M", weeks: 13 }, { label: "6M", weeks: 26 }, { label: "1Y", weeks: 52 }].map((r) => (
                        <button key={r.label} onClick={() => setHeatmapWeeks(r.weeks)} style={{ ...btnGhost, border: heatmapWeeks === r.weeks ? "2px solid #2563eb" : "2px solid #bfdbfe", background: heatmapWeeks === r.weeks ? "#e0f2fe" : "#f5faff", fontWeight: heatmapWeeks === r.weeks ? "bold" : "normal", padding: "4px 9px", fontSize: "12px" }}>{r.label}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <HabitHeatmap dailySelections={dailySelections || {}} revivedDays={revivedDays || {}} weeks={heatmapWeeks} />
                  </div>
                </div>
              );

            case "steps":
              if (stepsLog.length < 2) return null;
              return (
                <div key="steps">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={sectionTitle}>📈 Steps Chart</span>
                      {stepGoal && (
                        <span style={{ color: "#6b8cb0", fontSize: "12px" }}>
                          goal: {stepGoal.toLocaleString()}
                          {!habitStepGoal && (
                            <button type="button" onClick={() => { setStepGoalDraft(String(stepGoal)); setEditingStepGoal(true); }} style={{ marginLeft: "6px", background: "none", border: "none", color: "#93c5fd", cursor: "pointer", fontSize: "11px", padding: 0 }}>✏️</button>
                          )}
                        </span>
                      )}
                      {!stepGoal && !editingStepGoal && (
                        <button type="button" onClick={() => { setStepGoalDraft(""); setEditingStepGoal(true); }} style={{ ...btnGhost, padding: "3px 9px", fontSize: "12px" }}>+ Set step goal</button>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "6px" }}>
                      {STEPS_RANGES.map((r) => (
                        <button key={r.label} onClick={() => setStepsRange(r.months)} style={{ ...btnGhost, border: stepsRange === r.months ? "2px solid #2563eb" : "2px solid #bfdbfe", background: stepsRange === r.months ? "#e0f2fe" : "#f5faff", fontWeight: stepsRange === r.months ? "bold" : "normal", padding: "4px 9px", fontSize: "12px" }}>{r.label}</button>
                      ))}
                    </div>
                  </div>
                  {editingStepGoal && (
                    <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" }}>
                      <input type="text" placeholder="e.g. 8000 or 8k" value={stepGoalDraft} onChange={(e) => setStepGoalDraft(e.target.value)} style={{ ...inputStyle, maxWidth: "140px" }} autoFocus />
                      <button type="button" onClick={() => { const raw = stepGoalDraft.trim(); const kMatch = raw.match(/(\d+(?:\.\d+)?)\s*k/i); let parsed = null; if (kMatch) { const n = Math.round(parseFloat(kMatch[1]) * 1000); if (n >= 500 && n <= 200000) parsed = n; } else { const pm = raw.match(/\b(\d{1,3}(?:,\d{3}|\d)*)\b/); if (pm) { const n = parseInt(pm[1].replace(/,/g, ""), 10); if (n >= 500 && n <= 200000) parsed = n; } } if (parsed) setManualStepGoal(parsed); setEditingStepGoal(false); }} style={{ ...btnPrimary, padding: "8px 14px" }}>Save</button>
                      <button type="button" onClick={() => setEditingStepGoal(false)} style={{ ...btnGhost, padding: "8px 12px" }}>Cancel</button>
                      {manualStepGoal && <button type="button" onClick={() => { setManualStepGoal(null); setEditingStepGoal(false); }} style={{ ...btnGhost, padding: "8px 12px", color: "#dc2626", borderColor: "#fca5a5" }}>Remove</button>}
                    </div>
                  )}
                  {stepsChartData.length >= 2 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={stepsChartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#dbeafe" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6b8cb0" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 11, fill: "#6b8cb0" }} tickLine={false} axisLine={false} domain={stepGoal ? [0, (dataMax) => Math.max(dataMax, stepGoal) * 1.1] : ["auto", "auto"]} width={44} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                        <Tooltip contentStyle={{ background: "#f5faff", border: "2px solid #bfdbfe", borderRadius: "12px", fontSize: "13px", color: "#1e3a5f" }} formatter={(v) => [Number(v).toLocaleString(), "Steps"]} />
                        {stepGoal && <ReferenceLine y={stepGoal} stroke="#f59e0b" strokeDasharray="6 3" strokeWidth={2} label={{ value: `Goal: ${stepGoal.toLocaleString()}`, position: "insideTopRight", fill: "#d97706", fontSize: 11, fontWeight: "bold" }} />}
                        <Line type="monotone" dataKey="steps" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3, fill: "#2563eb" }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <div style={{ color: "#6b8cb0", fontSize: "13px" }}>No entries in this range.</div>}
                </div>
              );

            case "sideQuests":
              return (
                <div key="sideQuests">
                  <h3 style={{ margin: "0 0 4px", color: "#1e3a5f", fontSize: "17px" }}>🗓️ Side Quest Consistency</h3>
                  <p style={{ margin: "0 0 14px", color: "#6b8cb0", fontSize: "13px" }}>How often you've completed each side quest over the past 6 months.</p>
                  <SideQuestHeatmap sideQuestCompletions={sideQuestCompletions || {}} activeSideQuests={activeSideQuests || []} />
                </div>
              );

            case "aboutYou":
              return (
                <div key="aboutYou">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: editingProfile ? "16px" : "0" }}>
                    <span style={sectionTitle}>About You</span>
                    {!editingProfile && (
                      <button onClick={() => { setHeightFt(stats.heightFt != null ? String(stats.heightFt) : ""); setHeightIn(stats.heightIn != null ? String(stats.heightIn) : ""); setBirthday(stats.birthday ?? ""); setEditingProfile(true); }} style={{ ...btnGhost, padding: "4px 10px", fontSize: "13px" }}>✏️ Edit</button>
                    )}
                  </div>
                  {editingProfile ? (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
                        <div><div style={labelStyle}>Height</div><div style={{ display: "flex", gap: "6px" }}><input type="number" placeholder="ft" value={heightFt} onChange={(e) => setHeightFt(e.target.value)} style={{ ...inputStyle, width: "60px" }} min="0" /><input type="number" placeholder="in" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} style={{ ...inputStyle, width: "60px" }} min="0" max="11" /></div></div>
                        <div><div style={labelStyle}>Birthday</div><input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} style={inputStyle} /></div>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button onClick={handleSaveProfile} style={btnPrimary} disabled={profileSaving}>{profileSaving ? "Saving..." : "Save"}</button>
                        <button onClick={() => setEditingProfile(false)} style={btnGhost}>Cancel</button>
                      </div>
                    </>
                  ) : hasProfile ? (
                    <div style={{ display: "flex", gap: "24px", marginTop: "10px", flexWrap: "wrap" }}>
                      {stats.heightFt != null && <div><span style={{ color: "#6b8cb0", fontSize: "12px" }}>Height </span><span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{stats.heightFt}′ {stats.heightIn ?? 0}″</span></div>}
                      {stats.birthday && <div><span style={{ color: "#6b8cb0", fontSize: "12px" }}>Age </span><span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{age}</span></div>}
                      {stats.birthday && <div><span style={{ color: "#6b8cb0", fontSize: "12px" }}>Birthday </span><span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{formatDate(stats.birthday)}</span></div>}
                    </div>
                  ) : <div style={{ color: "#6b8cb0", fontSize: "13px", marginTop: "10px" }}>Tap Edit to add your height and birthday.</div>}
                </div>
              );

            case "weight": {
              const weightXpToday = getMetricXpForDate("weight", todayStr());
              const weightDueToday = isMetricDueToday("weight");
              return (
                <div key="weight" id="you-weight-section">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={sectionTitle}>Weight</span>
                      {latestWeight && <span style={{ color: "#6b8cb0", fontSize: "13px" }}>{latestWeight.weight} {latestWeight.unit}</span>}
                      {weightDueToday && <span style={{ background: "#fef9c3", color: "#92400e", fontSize: "11px", fontWeight: "bold", padding: "2px 7px", borderRadius: "999px", border: "1px solid #fde68a" }}>Due today</span>}
                      {weightXpToday > 0 && <span style={{ background: "#dcfce7", color: "#166534", fontSize: "11px", fontWeight: "bold", padding: "2px 7px", borderRadius: "999px", border: "1px solid #bbf7d0" }}>+{weightXpToday} XP</span>}
                    </div>
                    {!addingWeight && <button onClick={() => setAddingWeight(true)} style={{ ...btnGhost, padding: "4px 10px" }}>+ Log</button>}
                  </div>
                  {addingWeight && (
                    <div style={{ marginBottom: "16px", padding: "14px", borderRadius: "14px", background: "#f8fbff", border: "1px solid #bfdbfe" }}>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div><div style={{ ...labelStyle, marginBottom: "4px" }}>Weight</div><input type="number" placeholder="e.g. 150" value={weightInput} onChange={(e) => setWeightInput(e.target.value)} onFocus={(e) => e.target.select()} style={{ ...inputStyle, width: "80px" }} min="0" /></div>
                        <div><div style={{ ...labelStyle, marginBottom: "4px" }}>Unit</div><select value={weightUnit} onChange={(e) => setWeightUnit(e.target.value)} style={{ ...inputStyle, width: "70px" }}><option value="lbs">lbs</option><option value="kg">kg</option></select></div>
                        <div style={{ flex: 1, minWidth: "130px" }}><div style={{ ...labelStyle, marginBottom: "4px" }}>Date</div><input type="date" value={weightDate} onChange={(e) => setWeightDate(e.target.value)} style={inputStyle} /></div>
                      </div>
                      {weightError && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "8px 12px", color: "#dc2626", fontSize: "13px", marginTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          {weightError}
                          <button onClick={() => setWeightError("")} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "16px", lineHeight: 1, flexShrink: 0 }}>×</button>
                        </div>
                      )}
                      <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                        <button onClick={handleAddWeight} style={btnPrimary} disabled={weightSaving}>{weightSaving ? "Saving..." : "Save"}</button>
                        <button onClick={() => { setAddingWeight(false); setWeightInput(""); setWeightError(""); }} style={btnGhost}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {weightLog.length > 0 && (
                    <>
                      <div style={{ display: "flex", gap: "6px", marginBottom: "12px", flexWrap: "wrap" }}>
                        {WEIGHT_RANGES.map((r) => <button key={r.label} onClick={() => setWeightRange(r.months)} style={{ ...btnGhost, border: weightRange === r.months ? "2px solid #2563eb" : "2px solid #bfdbfe", background: weightRange === r.months ? "#e0f2fe" : "#f5faff", fontWeight: weightRange === r.months ? "bold" : "normal", padding: "5px 10px" }}>{r.label}</button>)}
                      </div>
                      {chartData.length >= 2 ? (
                        <div style={{ marginBottom: "14px" }}>
                          <ResponsiveContainer width="100%" height={180}>
                            <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#dbeafe" />
                              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6b8cb0" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                              <YAxis tick={{ fontSize: 11, fill: "#6b8cb0" }} tickLine={false} axisLine={false} domain={["auto", "auto"]} width={34} />
                              <Tooltip contentStyle={{ background: "#f5faff", border: "2px solid #bfdbfe", borderRadius: "12px", fontSize: "13px", color: "#1e3a5f" }} formatter={(v, _n, props) => [`${v} ${props?.payload?.unit ?? ""}`, "Weight"]} />
                              <Line type="monotone" dataKey="weight" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3, fill: "#2563eb" }} activeDot={{ r: 5 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : chartData.length === 1 ? <div style={{ color: "#6b8cb0", fontSize: "13px", marginBottom: "12px" }}>Log at least 2 entries to see the chart.</div>
                        : <div style={{ color: "#6b8cb0", fontSize: "13px", marginBottom: "12px" }}>No entries in this range.</div>}
                      <button onClick={() => setWeightExpanded((p) => !p)} style={{ ...btnGhost, width: "100%", textAlign: "center", marginTop: "4px" }}>{weightExpanded ? "▲ Hide log" : `▼ Show log (${weightLog.length})`}</button>
                      {weightExpanded && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "8px" }}>
                          {[...weightLog].reverse().map((e) => (
                            <div key={e.id} style={logRow}>
                              <span style={{ color: "#6b8cb0" }}>{formatDate(e.date)}</span>
                              <span style={{ fontWeight: "bold" }}>{e.weight} {e.unit}</span>
                              {deleteWeightConfirm === e.id ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <span style={{ fontSize: "12px", color: "#991b1b", fontWeight: "bold" }}>Delete?</span>
                                  <button onClick={() => { handleDeleteWeight(e.id); setDeleteWeightConfirm(null); }} style={{ padding: "2px 8px", borderRadius: "6px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>Yes</button>
                                  <button onClick={() => setDeleteWeightConfirm(null)} style={{ padding: "2px 8px", borderRadius: "6px", border: "1px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>No</button>
                                </div>
                              ) : (
                                <button onClick={() => setDeleteWeightConfirm(e.id)} style={{ ...btnGhost, padding: "2px 8px", fontSize: "12px", border: "none", background: "none", color: "#93c5fd" }}>✕</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {weightLog.length === 0 && !addingWeight && <div style={{ color: "#6b8cb0", fontSize: "13px" }}>No weight entries yet.</div>}
                </div>
              );
            }

            case "measurements": {
              const measureXpToday = getMetricXpForDate("measurements", todayStr());
              const measureDueToday = isMetricDueToday("measurements");
              return (
                <div key="measurements" id="you-measurements-section">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={sectionTitle}>Measurements</span>
                      <span style={{ color: "#6b8cb0", fontSize: "12px" }}>inches</span>
                      {measureDueToday && <span style={{ background: "#fef9c3", color: "#92400e", fontSize: "11px", fontWeight: "bold", padding: "2px 7px", borderRadius: "999px", border: "1px solid #fde68a" }}>Due today</span>}
                      {measureXpToday > 0 && <span style={{ background: "#dcfce7", color: "#166534", fontSize: "11px", fontWeight: "bold", padding: "2px 7px", borderRadius: "999px", border: "1px solid #bbf7d0" }}>+{measureXpToday} XP</span>}
                    </div>
                    {!addingMeasurements && <button onClick={() => setAddingMeasurements(true)} style={{ ...btnGhost, padding: "4px 10px" }}>+ Log</button>}
                  </div>
                  {latestMeasurements && !addingMeasurements && (
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "14px" }}>
                      {MEASUREMENT_KEYS.map((k) => latestMeasurements[k] != null ? <div key={k} style={{ padding: "6px 12px", borderRadius: "999px", background: "#e0f2fe", border: "2px solid #7dd3fc", fontSize: "13px", color: "#1e3a5f", fontWeight: "bold" }}>{k.charAt(0).toUpperCase() + k.slice(1)}: {latestMeasurements[k]}″</div> : null)}
                    </div>
                  )}
                  {addingMeasurements && (
                    <div style={{ marginBottom: "16px", padding: "14px", borderRadius: "14px", background: "#f8fbff", border: "1px solid #bfdbfe" }}>
                      <div style={{ marginBottom: "10px" }}><div style={{ ...labelStyle, marginBottom: "4px" }}>Date</div><input type="date" value={measureDate} onChange={(e) => setMeasureDate(e.target.value)} style={{ ...inputStyle, maxWidth: "180px" }} /></div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>{MEASUREMENT_KEYS.map((k) => <div key={k}><div style={{ ...labelStyle, marginBottom: "4px" }}>{k.charAt(0).toUpperCase() + k.slice(1)}</div><input type="number" placeholder="—" value={measureInputs[k]} onChange={(e) => setMeasureInputs((prev) => ({ ...prev, [k]: e.target.value }))} onFocus={(e) => e.target.select()} style={inputStyle} min="0" /></div>)}</div>
                      {measureError && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "8px 12px", color: "#dc2626", fontSize: "13px", marginBottom: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          {measureError}
                          <button onClick={() => setMeasureError("")} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "16px", lineHeight: 1, flexShrink: 0 }}>×</button>
                        </div>
                      )}
                      <div style={{ display: "flex", gap: "8px" }}><button onClick={handleAddMeasurements} style={btnPrimary} disabled={measureSaving}>{measureSaving ? "Saving..." : "Save"}</button><button onClick={() => { setAddingMeasurements(false); setMeasureInputs({ chest: "", shoulders: "", waist: "", hip: "" }); setMeasureError(""); }} style={btnGhost}>Cancel</button></div>
                    </div>
                  )}
                  {measurementsLog.length > 0 && !addingMeasurements && (
                    <>
                      <button onClick={() => setMeasurementsExpanded((p) => !p)} style={{ ...btnGhost, width: "100%", textAlign: "center", marginTop: "4px" }}>{measurementsExpanded ? "▲ Hide log" : `▼ Show log (${measurementsLog.length})`}</button>
                      {measurementsExpanded && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "8px" }}>
                          {measurementsLog.map((e) => (
                            <div key={e.id} style={{ ...logRow, flexWrap: "wrap", gap: "6px" }}>
                              <span style={{ color: "#6b8cb0", flexShrink: 0 }}>{formatDate(e.date)}</span>
                              <div style={{ display: "flex", gap: "10px", flex: 1, flexWrap: "wrap" }}>
                                {MEASUREMENT_KEYS.map((k) => e[k] != null ? <span key={k} style={{ fontSize: "13px" }}><span style={{ color: "#6b8cb0" }}>{k.slice(0, 2)} </span><span style={{ fontWeight: "bold" }}>{e[k]}″</span></span> : null)}
                              </div>
                              {deleteMeasureConfirm === e.id ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <span style={{ fontSize: "12px", color: "#991b1b", fontWeight: "bold" }}>Delete?</span>
                                  <button onClick={() => { handleDeleteMeasurements(e.id); setDeleteMeasureConfirm(null); }} style={{ padding: "2px 8px", borderRadius: "6px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>Yes</button>
                                  <button onClick={() => setDeleteMeasureConfirm(null)} style={{ padding: "2px 8px", borderRadius: "6px", border: "1px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>No</button>
                                </div>
                              ) : (
                                <button onClick={() => setDeleteMeasureConfirm(e.id)} style={{ ...btnGhost, padding: "2px 8px", fontSize: "12px", border: "none", background: "none", color: "#93c5fd" }}>✕</button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {measurementsLog.length === 0 && !addingMeasurements && <div style={{ color: "#6b8cb0", fontSize: "13px" }}>No measurements logged yet.</div>}
                </div>
              );
            }

            case "stepsLog":
              return (
                <div key="stepsLog">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <div><span style={sectionTitle}>Steps Log</span>{stepsLog.length > 0 && <span style={{ color: "#6b8cb0", fontSize: "13px", marginLeft: "10px" }}>{Number(stepsLog[stepsLog.length - 1].steps).toLocaleString()} today</span>}</div>
                    {!addingSteps && (
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <button onClick={handleConnectAndSyncSteps} style={{ ...btnGhost, padding: "4px 10px" }} disabled={healthSyncing || (!isAndroidNative() && !isIosNative())} title={isIosNative() ? "Sync today's steps from Apple HealthKit" : isAndroidNative() ? "Sync today's steps from Health Connect" : "Available on Android and iOS app builds only"}>{healthSyncing ? "Syncing..." : "Sync device"}</button>
                        <button onClick={() => setAddingSteps(true)} style={{ ...btnGhost, padding: "4px 10px" }}>+ Log</button>
                      </div>
                    )}
                  </div>
                  <div style={{ color: healthConnectAvailable ? "#2563eb" : "#6b8cb0", fontSize: "12px", marginTop: "-6px", marginBottom: "6px" }}>{healthConnectStatus}</div>
                  <div style={{ marginBottom: "12px" }}>
                    <button onClick={() => setShowFitbitHelp((p) => !p)} style={{ ...btnGhost, fontSize: "12px", padding: "4px 10px", display: "flex", alignItems: "center", gap: "6px" }}><span>⌚ Connect a wearable?</span><span style={{ fontSize: "10px" }}>{showFitbitHelp ? "▲" : "▼"}</span></button>
                    {showFitbitHelp && (
                      <div style={{ marginTop: "8px", padding: "14px", borderRadius: "14px", background: "#eff6ff", border: "2px solid #bfdbfe", fontSize: "13px", color: "#1e3a5f", lineHeight: "1.6" }}>
                        <div style={{ fontWeight: "bold", marginBottom: "10px" }}>📲 How to sync your wearable</div>
                        <div style={{ marginBottom: "12px" }}><div style={{ fontWeight: "bold", color: "#6d28d9", marginBottom: "6px" }}>🍎 Apple Watch / iPhone (iOS)</div><div style={{ display: "flex", flexDirection: "column", gap: "5px" }}><div style={{ display: "flex", gap: "8px" }}><span style={{ background: "#6d28d9", color: "#fff", borderRadius: "999px", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "bold", flexShrink: 0 }}>1</span><span>Steps are tracked automatically by iPhone or Apple Watch via <strong>Apple Health</strong> — no setup needed.</span></div><div style={{ display: "flex", gap: "8px" }}><span style={{ background: "#6d28d9", color: "#fff", borderRadius: "999px", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "bold", flexShrink: 0 }}>2</span><span>Tap <strong>Sync device</strong> above and grant permission when asked.</span></div></div></div>
                        <div style={{ marginBottom: "12px" }}><div style={{ fontWeight: "bold", color: "#2563eb", marginBottom: "6px" }}>🟣 Fitbit (Android)</div><div style={{ display: "flex", flexDirection: "column", gap: "5px" }}><div style={{ display: "flex", gap: "8px" }}><span style={{ background: "#2563eb", color: "#fff", borderRadius: "999px", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "bold", flexShrink: 0 }}>1</span><span>Open <strong>Fitbit app</strong> → Profile → App Settings → <strong>Health Connect</strong> → enable <em>Steps</em>.</span></div><div style={{ display: "flex", gap: "8px" }}><span style={{ background: "#2563eb", color: "#fff", borderRadius: "999px", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "bold", flexShrink: 0 }}>2</span><span>Sync your Fitbit, then tap <strong>Sync device</strong> above.</span></div></div></div>
                        <div style={{ marginBottom: "10px" }}><div style={{ fontWeight: "bold", color: "#16a34a", marginBottom: "6px" }}>🟢 Garmin (Android)</div><div style={{ display: "flex", flexDirection: "column", gap: "5px" }}><div style={{ display: "flex", gap: "8px" }}><span style={{ background: "#16a34a", color: "#fff", borderRadius: "999px", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "bold", flexShrink: 0 }}>1</span><span>Open <strong>Garmin Connect app</strong> → More → Settings → <strong>Health Connect</strong> → enable <em>Steps</em>.</span></div><div style={{ display: "flex", gap: "8px" }}><span style={{ background: "#16a34a", color: "#fff", borderRadius: "999px", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "bold", flexShrink: 0 }}>2</span><span>Sync your Garmin watch, then tap <strong>Sync device</strong> above.</span></div></div></div>
                        <div style={{ color: "#4b6b8a", fontSize: "12px", borderTop: "1px solid #bfdbfe", paddingTop: "8px", marginTop: "4px" }}>ℹ️ iOS uses Apple HealthKit. Android uses Health Connect. No extra login needed in this app.</div>
                      </div>
                    )}
                  </div>
                  {addingSteps && (
                    <div style={{ marginBottom: "16px", padding: "14px", borderRadius: "14px", background: "#f8fbff", border: "1px solid #bfdbfe" }}>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ flex: 1, minWidth: "100px" }}><div style={{ ...labelStyle, marginBottom: "4px" }}>Steps</div><input type="number" placeholder="e.g. 8000" value={stepsInput} onChange={(e) => setStepsInput(e.target.value)} style={inputStyle} min="0" /></div>
                        <div style={{ flex: 1, minWidth: "130px" }}><div style={{ ...labelStyle, marginBottom: "4px" }}>Date</div><input type="date" value={stepsDate} onChange={(e) => setStepsDate(e.target.value)} style={inputStyle} /></div>
                      </div>
                      {stepsError && (
                        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "8px 12px", color: "#dc2626", fontSize: "13px", marginTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          {stepsError}
                          <button onClick={() => setStepsError("")} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "16px", lineHeight: 1, flexShrink: 0 }}>×</button>
                        </div>
                      )}
                      <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}><button onClick={handleAddSteps} style={btnPrimary} disabled={stepsSaving}>{stepsSaving ? "Saving..." : "Save"}</button><button onClick={() => { setAddingSteps(false); setStepsInput(""); setStepsError(""); }} style={btnGhost}>Cancel</button></div>
                    </div>
                  )}
                  {stepsLog.length > 0 && !addingSteps && (
                    <>
                      <button onClick={() => setStepsExpanded((p) => !p)} style={{ ...btnGhost, width: "100%", textAlign: "center", marginTop: "4px" }}>{stepsExpanded ? "▲ Hide log" : `▼ Show log (${stepsLog.length})`}</button>
                      {stepsExpanded && <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "8px" }}>{[...stepsLog].reverse().map((e) => <div key={e.id} style={logRow}><span style={{ color: "#6b8cb0" }}>{formatDate(e.date)}</span><span style={{ fontWeight: "bold" }}>{Number(e.steps).toLocaleString()} steps{e.source === "health-connect" ? " • HC" : ""}</span><button onClick={() => handleDeleteSteps(e.id)} style={{ ...btnGhost, padding: "2px 8px", fontSize: "12px", border: "none", background: "none", color: "#93c5fd" }}>✕</button></div>)}</div>}
                    </>
                  )}
                  {stepsLog.length === 0 && !addingSteps && <div style={{ color: "#6b8cb0", fontSize: "13px" }}>No steps logged yet.</div>}

                  {/* ── Health Sync Settings ─────────────────────────────── */}
                  <div style={{ marginTop: "22px", borderTop: "1px solid #dbeafe", paddingTop: "18px" }}>
                    <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px", marginBottom: "14px" }}>
                      {isIosNative() ? "Apple Health Sync" : isAndroidNative() ? "Google Health Connect Sync" : "Health App Sync"}
                    </div>

                    {/* Workout toggle */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: "12px", background: "#f0f9ff", border: "1px solid #bfdbfe", marginBottom: "10px" }}>
                      <div>
                        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>Auto-log Cardio Workouts</div>
                        <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "2px" }}>Workouts recorded by Apple Health or Health Connect are automatically added to your cardio log</div>
                      </div>
                      <button
                        onClick={() => handleToggleHealthSyncPref("workouts", !healthSyncPrefs.workouts)}
                        style={{
                          width: "46px", height: "26px", borderRadius: "13px", border: "none",
                          background: healthSyncPrefs.workouts ? "#2563eb" : "#cbd5e1",
                          cursor: "pointer", position: "relative", flexShrink: 0, marginLeft: "12px",
                          transition: "background 0.2s",
                        }}
                      >
                        <div style={{
                          width: "20px", height: "20px", borderRadius: "50%", background: "#fff",
                          position: "absolute", top: "3px",
                          left: healthSyncPrefs.workouts ? "23px" : "3px",
                          transition: "left 0.2s",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        }} />
                      </button>
                    </div>

                    {/* Auto side quests — only shown when workouts is on */}
                    {healthSyncPrefs.workouts && (
                      <div style={{ borderRadius: "12px", background: "#f0f9ff", border: "1px solid #bfdbfe", marginBottom: "10px", marginLeft: "12px", overflow: "hidden" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px" }}>
                          <div>
                            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>Auto-complete Side Quest</div>
                            <div style={{ color: "#4b6b8a", fontSize: "12px", marginTop: "2px" }}>Automatically check off a side quest when a workout syncs</div>
                          </div>
                          <button
                            onClick={() => handleToggleHealthSyncPref("autoSideQuests", !healthSyncPrefs.autoSideQuests)}
                            style={{
                              width: "46px", height: "26px", borderRadius: "13px", border: "none",
                              background: healthSyncPrefs.autoSideQuests ? "#2563eb" : "#cbd5e1",
                              cursor: "pointer", position: "relative", flexShrink: 0, marginLeft: "12px",
                              transition: "background 0.2s",
                            }}
                          >
                            <div style={{
                              width: "20px", height: "20px", borderRadius: "50%", background: "#fff",
                              position: "absolute", top: "3px",
                              left: healthSyncPrefs.autoSideQuests ? "23px" : "3px",
                              transition: "left 0.2s",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                            }} />
                          </button>
                        </div>
                        {healthSyncPrefs.autoSideQuests && (
                          <div style={{ padding: "0 14px 14px" }}>
                            <div style={{ color: "#4b6b8a", fontSize: "12px", marginBottom: "6px" }}>Which side quest should sync check off?</div>
                            <select
                              value={healthSyncPrefs.autoSideQuestTitle || ""}
                              onChange={(e) => handleToggleHealthSyncPref("autoSideQuestTitle", e.target.value || null)}
                              style={{ width: "100%", padding: "8px 10px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#fff", color: "#1e3a5f", fontSize: "14px", outline: "none" }}
                            >
                              <option value="">— Pick a side quest —</option>
                              {(activeSideQuests || []).map((q) => (
                                <option key={q.title} value={q.title}>{q.emoji ? `${q.emoji} ` : ""}{q.title}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sync now button */}
                    {healthSyncPrefs.workouts && (isAndroidNative() || isIosNative()) && (
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "4px" }}>
                        <button
                          onClick={handleSyncWorkoutsNow}
                          disabled={healthSyncLoading}
                          style={{ ...btnPrimary, fontSize: "13px", padding: "8px 16px" }}
                        >
                          {healthSyncLoading ? "Syncing..." : "Sync Workouts Now"}
                        </button>
                        {healthSyncStatus ? (
                          <div style={{ color: "#4b6b8a", fontSize: "12px" }}>{healthSyncStatus}</div>
                        ) : null}
                      </div>
                    )}

                    {!isAndroidNative() && !isIosNative() && (
                      <div style={{ color: "#6b8cb0", fontSize: "12px", marginTop: "4px" }}>Health sync is available on the Android and iOS app builds.</div>
                    )}
                  </div>
                </div>
              );

            case "strength":
              if (!isMomentum) return null;
              return (
                <div key="strength">
                  <h3 style={{ margin: "0 0 14px", color: "#1e3a5f", fontSize: "17px" }}>📈 Strength Progress</h3>
                  <ExerciseStrengthChart userId={uid} />
                </div>
              );

            default:
              return null;
          }
        }

        return (
          <>
            {/* Reorder hint */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <div style={{ color: "#6b8cb0", fontSize: "12px" }}>
                {reorderMode ? "Tap ▲ ▼ to move sections. Tap Done when finished." : "Hold any section to reorder"}
              </div>
              {reorderMode && (
                <button
                  type="button"
                  onClick={() => { setReorderMode(false); setDraggingIdx(null); }}
                  style={{ padding: "6px 14px", borderRadius: "10px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}
                >
                  Done
                </button>
              )}
            </div>

            {orderedKeys.map((key, idx) => {
              const content = renderSection(key);
              if (!content) return null;

              const isDragging = draggingIdx === idx;

              return (
                <div
                  key={key}
                  style={{
                    ...card,
                    position: "relative",
                    border: isDragging ? "2px solid #2563eb" : reorderMode ? "2px dashed #93c5fd" : "2px solid #dbeafe",
                    background: isDragging ? "#e0f2fe" : "#f5faff",
                    transition: "border 0.15s, background 0.15s",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                  }}
                  onMouseDown={() => {
                    longPressTimer.current = setTimeout(() => {
                      setReorderMode(true);
                      setDraggingIdx(idx);
                    }, 500);
                  }}
                  onMouseUp={() => { clearTimeout(longPressTimer.current); }}
                  onMouseLeave={() => { clearTimeout(longPressTimer.current); }}
                  onTouchStart={() => {
                    longPressTimer.current = setTimeout(() => {
                      setReorderMode(true);
                      setDraggingIdx(idx);
                    }, 500);
                  }}
                  onTouchEnd={() => { clearTimeout(longPressTimer.current); }}
                  onTouchMove={() => { clearTimeout(longPressTimer.current); }}
                >
                  {/* Reorder controls overlay */}
                  {reorderMode && (
                    <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", flexDirection: "column", gap: "4px", zIndex: 10 }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moveSection(idx, idx - 1); }}
                        disabled={idx === 0}
                        style={{ width: "28px", height: "28px", borderRadius: "8px", border: "none", background: idx === 0 ? "#e0e7ef" : "#2563eb", color: idx === 0 ? "#9ca3af" : "#fff", fontWeight: "bold", fontSize: "14px", cursor: idx === 0 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >▲</button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moveSection(idx, idx + 1); }}
                        disabled={idx === orderedKeys.length - 1}
                        style={{ width: "28px", height: "28px", borderRadius: "8px", border: "none", background: idx === orderedKeys.length - 1 ? "#e0e7ef" : "#2563eb", color: idx === orderedKeys.length - 1 ? "#9ca3af" : "#fff", fontWeight: "bold", fontSize: "14px", cursor: idx === orderedKeys.length - 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >▼</button>
                    </div>
                  )}
                  {/* Drag handle indicator */}
                  {reorderMode && (
                    <div style={{ position: "absolute", top: "10px", left: "10px", color: "#93c5fd", fontSize: "16px", lineHeight: 1 }}>⠿</div>
                  )}
                  <div style={{ paddingRight: reorderMode ? "44px" : "0", paddingLeft: reorderMode ? "24px" : "0" }}>
                    {content}
                  </div>
                </div>
              );
            })}

          </>
        );
      })()}

      {/* ════════════════════ RECAP TAB ════════════════════ */}
      {subTab === "recap" && (() => {
        const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

        function getMonday(d) {
          const day = d.getDay();
          const diff = day === 0 ? -6 : 1 - day;
          const m = new Date(d);
          m.setDate(d.getDate() + diff);
          m.setHours(0, 0, 0, 0);
          return m;
        }

        function buildDays(monday) {
          return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(monday);
            d.setDate(d.getDate() + i);
            return d.toISOString().slice(0, 10);
          });
        }

        function fmtShort(dk) {
          const [, m, d] = dk.split("-");
          return `${parseInt(m)}/${parseInt(d)}`;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thisMonday = getMonday(today);
        const lastMonday = new Date(thisMonday);
        lastMonday.setDate(thisMonday.getDate() - 7);

        const lastWeekDays = buildDays(lastMonday);
        const thisWeekDays = buildDays(thisMonday);
        const todayKey = today.toISOString().slice(0, 10);

        function weekStats(days) {
          const habits = customHabits || {};
          const activeWeeklyChallenges = (gameContent?.weeklyChallenges || []).filter(c => c.active);
          const activeSideQuestList = (gameContent?.sideQuests || []).filter(q => q.active);
          const sqXP = Object.fromEntries(activeSideQuestList.map(q => [q.title, q.xp || 0]));
          const weekStart = new Date(`${days[0]}T12:00:00`);
          const seed = Math.floor(weekStart.getTime() / (1000 * 60 * 60 * 24 * 7));
          const weekChallenge = activeWeeklyChallenges.length > 0
            ? activeWeeklyChallenges[Math.abs(seed) % activeWeeklyChallenges.length]
            : null;

          let fullDays = 0, nutritionDays = 0, movementDays = 0, mindsetDays = 0;
          let weeklyChallengeDays = 0;
          let totalXP = 0;
          const completedSideQuests = [];
          const levelCounts = {
            nutrition: { low: 0, main: 0, high: 0 },
            movement:  { low: 0, main: 0, high: 0 },
            mindset:   { low: 0, main: 0, high: 0 },
          };

          days.forEach(dk => {
            const sel = dailySelections?.[dk] || {};
            if (sel.nutrition) { nutritionDays++; totalXP += habits.nutrition?.[sel.nutrition]?.xp || 0; if (levelCounts.nutrition[sel.nutrition] !== undefined) levelCounts.nutrition[sel.nutrition]++; }
            if (sel.movement)  { movementDays++;  totalXP += habits.movement?.[sel.movement]?.xp   || 0; if (levelCounts.movement[sel.movement]   !== undefined) levelCounts.movement[sel.movement]++;   }
            if (sel.mindset)   { mindsetDays++;   totalXP += habits.mindset?.[sel.mindset]?.xp    || 0; if (levelCounts.mindset[sel.mindset]     !== undefined) levelCounts.mindset[sel.mindset]++;     }
            if (sel.nutrition && sel.movement && sel.mindset) fullDays++;
            if (sel.weekly) { weeklyChallengeDays++; totalXP += weekChallenge?.xp || 0; }
            const sq = sideQuestCompletions?.[dk] || {};
            Object.entries(sq).forEach(([title, done]) => {
              if (done) { completedSideQuests.push({ title, dateKey: dk, xp: sqXP[title] || 0 }); totalXP += sqXP[title] || 0; }
            });
          });

          return { fullDays, nutritionDays, movementDays, mindsetDays, weeklyChallengeDays, weekChallenge, completedSideQuests, totalXP, levelCounts };
        }

        function headline(fullDays) {
          if (fullDays >= 7) return "🏆 Perfect week!";
          if (fullDays >= 5) return "🔥 Strong week!";
          if (fullDays >= 3) return "💪 Solid effort!";
          if (fullDays >= 1) return "🌱 Keep building!";
          return "📅 No full days";
        }

        const last = weekStats(lastWeekDays);
        const thisWk = weekStats(thisWeekDays.filter(dk => dk <= todayKey));

        const catLabels = {
          nutrition: categoryLabels?.nutrition || "Nutrition",
          movement: categoryLabels?.movement || "Movement",
          mindset: categoryLabels?.mindset || "Mindset",
        };

        function StreakBar({ label, value, total, color }) {
          return (
            <div style={{ background: "rgba(255,255,255,0.75)", borderRadius: "14px", padding: "10px 12px", border: "1px solid #bfdbfe" }}>
              <div style={{ color: "#6b8cb0", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "3px", marginTop: "2px" }}>
                <span style={{ color, fontWeight: "bold", fontSize: "22px" }}>{value}</span>
                <span style={{ color: "#4b6b8a", fontSize: "13px" }}>/ {total}</span>
              </div>
              <div style={{ height: "5px", borderRadius: "999px", background: "#dbeafe", marginTop: "6px" }}>
                <div style={{ height: "100%", borderRadius: "999px", background: color, width: `${total > 0 ? (value / total) * 100 : 0}%`, transition: "width 0.4s" }} />
              </div>
            </div>
          );
        }

        function DayDots({ days, upToKey }) {
          return (
            <div style={{ display: "flex", gap: "5px", marginBottom: "14px" }}>
              {days.map((dk, idx) => {
                const sel = dailySelections?.[dk] || {};
                const future = upToKey && dk > upToKey;
                const full = !future && !!sel.nutrition && !!sel.movement && !!sel.mindset;
                const partial = !future && !full && (!!sel.nutrition || !!sel.movement || !!sel.mindset);
                return (
                  <div key={dk} style={{ flex: 1, textAlign: "center" }}>
                    <div style={{
                      width: "100%", aspectRatio: "1/1", borderRadius: "10px",
                      background: future ? "#f1f5f9" : full ? "#2563eb" : partial ? "#93c5fd" : "#dbeafe",
                      border: future ? "2px dashed #cbd5e1" : full ? "2px solid #1d4ed8" : "2px solid #bfdbfe",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "13px", color: full ? "#fff" : "#93c5fd",
                    }}>
                      {!future && (full ? "✓" : partial ? "·" : "")}
                    </div>
                    <div style={{ color: "#6b8cb0", fontSize: "10px", marginTop: "3px" }}>{DAY_LABELS[idx]}</div>
                  </div>
                );
              })}
            </div>
          );
        }

        return (
          <div style={{ display: "grid", gap: "16px", paddingTop: "4px" }}>

            {/* ── Last Week ─────────────────────────────────── */}
            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "18px", marginBottom: "-6px" }}>
              {headline(last.fullDays)}
              <span style={{ fontWeight: "normal", fontSize: "13px", color: "#4b6b8a", marginLeft: "10px" }}>
                {fmtShort(lastWeekDays[0])} – {fmtShort(lastWeekDays[6])}
              </span>
            </div>

            {/* Keep the Streak */}
            <div style={{ background: "#f0f9ff", borderRadius: "20px", padding: "16px", border: "2px solid #7dd3fc" }}>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", marginBottom: "12px" }}>🔥 Keep the Streak</div>
              <DayDots days={lastWeekDays} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <StreakBar label="Full Days"            value={last.fullDays}      total={7} color="#2563eb" />
                <StreakBar label={catLabels.nutrition}  value={last.nutritionDays} total={7} color="#ef4444" />
                <StreakBar label={catLabels.movement}   value={last.movementDays}  total={7} color="#22c55e" />
                <StreakBar label={catLabels.mindset}    value={last.mindsetDays}   total={7} color="#a855f7" />
              </div>
            </div>

            {/* Weekly Challenge */}
            <div style={{ background: "#fefce8", borderRadius: "20px", padding: "16px", border: "2px solid #fde047" }}>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", marginBottom: "10px" }}>⭐ Weekly Challenge</div>
              <div style={{ marginBottom: "10px" }}>
                <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "15px" }}>
                  {last.weekChallenge?.title || "Weekly Challenge"}
                </div>
                {last.weekChallenge?.xp > 0 && (
                  <div style={{ color: "#854d0e", fontSize: "13px", marginTop: "2px" }}>💎 {last.weekChallenge.xp} XP</div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ flex: 1, height: "8px", borderRadius: "999px", background: "#fde68a", overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: "999px", background: last.weeklyChallengeDays > 0 ? "#ca8a04" : "#fde68a", width: `${(last.weeklyChallengeDays / 7) * 100}%`, transition: "width 0.4s" }} />
                </div>
                <div style={{
                  fontWeight: "bold", fontSize: "15px", flexShrink: 0,
                  color: last.weeklyChallengeDays >= 5 ? "#14532d" : last.weeklyChallengeDays >= 3 ? "#854d0e" : "#991b1b",
                }}>
                  {last.weeklyChallengeDays}/7 days
                </div>
              </div>
            </div>

            {/* Habit Breakdown */}
            <div style={{ background: "#f8fbff", borderRadius: "20px", padding: "16px", border: "2px solid #bfdbfe" }}>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", marginBottom: "12px" }}>📊 Habit Breakdown</div>
              <div style={{ display: "grid", gap: "10px" }}>
                {[
                  { key: "nutrition", label: catLabels.nutrition, color: "#ef4444", bg: "#fef2f2", border: "#fca5a5" },
                  { key: "movement",  label: catLabels.movement,  color: "#22c55e", bg: "#f0fdf4", border: "#86efac" },
                  { key: "mindset",   label: catLabels.mindset,   color: "#a855f7", bg: "#faf5ff", border: "#c4b5fd" },
                ].map(({ key, label, color, bg, border }) => {
                  const counts = last.levelCounts[key];
                  const total = lastWeekDays.length;
                  const missed = total - (counts.low + counts.main + counts.high);
                  const levels = [
                    { level: "high", label: customHabits?.[key]?.high?.habitName || "High", count: counts.high },
                    { level: "main", label: customHabits?.[key]?.main?.habitName || "Main", count: counts.main },
                    { level: "low",  label: customHabits?.[key]?.low?.habitName  || "Low",  count: counts.low  },
                  ].filter(l => l.count > 0);
                  return (
                    <div key={key} style={{ background: bg, borderRadius: "14px", padding: "12px 14px", border: `2px solid ${border}` }}>
                      <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "8px" }}>{label}</div>
                      <div style={{ display: "grid", gap: "5px" }}>
                        {levels.map(({ level, label: lvlLabel, count }) => (
                          <div key={level} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{ flex: 1, height: "6px", borderRadius: "999px", background: "rgba(0,0,0,0.08)", overflow: "hidden" }}>
                              <div style={{ height: "100%", borderRadius: "999px", background: color, width: `${(count / total) * 100}%`, opacity: level === "high" ? 1 : level === "main" ? 0.7 : 0.45 }} />
                            </div>
                            <div style={{ color: "#1e3a5f", fontSize: "12px", fontWeight: "bold", whiteSpace: "nowrap", minWidth: "24px", textAlign: "right" }}>{count}d</div>
                            <div style={{ color: "#4b6b8a", fontSize: "12px", minWidth: "80px" }}>{lvlLabel}</div>
                          </div>
                        ))}
                        {missed > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={{ flex: 1, height: "6px", borderRadius: "999px", background: "rgba(0,0,0,0.08)" }}>
                              <div style={{ height: "100%", borderRadius: "999px", background: "#cbd5e1", width: `${(missed / total) * 100}%` }} />
                            </div>
                            <div style={{ color: "#6b8cb0", fontSize: "12px", fontWeight: "bold", minWidth: "24px", textAlign: "right" }}>{missed}d</div>
                            <div style={{ color: "#6b8cb0", fontSize: "12px", minWidth: "80px" }}>Missed</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Main Quest — assignment */}
            <div style={{ background: "#f0fdf4", borderRadius: "20px", padding: "16px", border: "2px solid #86efac" }}>
              <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px", marginBottom: "10px" }}>📋 Main Quest</div>
              {recapPrompt === undefined ? (
                <div style={{ color: "#6b8cb0", fontSize: "14px" }}>Loading...</div>
              ) : recapPrompt === null ? (
                <div style={{ color: "#6b8cb0", fontSize: "14px" }}>No assignment this week</div>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "15px" }}>{recapPrompt.title}</div>
                    {recapPrompt.xpReward > 0 && (
                      <div style={{ color: "#14532d", fontSize: "13px", marginTop: "2px" }}>💎 {recapPrompt.xpReward} XP</div>
                    )}
                  </div>
                  <div style={{
                    padding: "6px 14px", borderRadius: "999px", fontWeight: "bold", fontSize: "14px", flexShrink: 0,
                    background: recapResponse?.isSubmitted ? "#bbf7d0" : "#fee2e2",
                    color: recapResponse?.isSubmitted ? "#14532d" : "#991b1b",
                    border: `2px solid ${recapResponse?.isSubmitted ? "#86efac" : "#fca5a5"}`,
                  }}>
                    {recapResponse?.isSubmitted ? "✓ Submitted" : "✗ Not yet"}
                  </div>
                </div>
              )}
            </div>

            {/* Extra Credits — side quests */}
            <div style={{ background: "#faf5ff", borderRadius: "20px", padding: "16px", border: "2px solid #c4b5fd" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>✨ Extra Credits</div>
                {last.completedSideQuests.length > 0 && (
                  <div style={{ color: "#7c3aed", fontWeight: "bold", fontSize: "13px" }}>
                    {last.completedSideQuests.length} completed
                  </div>
                )}
              </div>
              {last.completedSideQuests.length === 0 ? (
                <div style={{ color: "#6b8cb0", fontSize: "14px" }}>No side quests completed last week</div>
              ) : (
                <div style={{ display: "grid", gap: "8px" }}>
                  {last.completedSideQuests.map(({ title, dateKey, xp }, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      background: "rgba(255,255,255,0.75)", borderRadius: "12px", padding: "10px 14px",
                      border: "1px solid #ddd6fe",
                    }}>
                      <div style={{ color: "#1e3a5f", fontWeight: "bold", fontSize: "14px" }}>✓ {title}</div>
                      <div style={{ color: "#7c3aed", fontSize: "13px", fontWeight: "bold", flexShrink: 0, marginLeft: "10px" }}>
                        {xp > 0 ? `+${xp} XP` : fmtShort(dateKey)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* XP total */}
            {last.totalXP > 0 && (
              <div style={{
                background: "linear-gradient(135deg, #dbeafe, #e0f2fe)", borderRadius: "20px",
                padding: "16px 20px", border: "2px solid #93c5fd",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>💎 Total XP Last Week</div>
                <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "22px" }}>+{last.totalXP}</div>
              </div>
            )}

            {/* ── This Week So Far ──────────────────────────── */}
            <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "16px", marginTop: "4px", marginBottom: "-6px" }}>
              This Week So Far
              <span style={{ fontWeight: "normal", fontSize: "13px", color: "#4b6b8a", marginLeft: "10px" }}>
                {fmtShort(thisWeekDays[0])} – today
              </span>
            </div>

            <div style={{ background: "#f8fbff", borderRadius: "20px", padding: "16px", border: "2px solid #bfdbfe" }}>
              <DayDots days={thisWeekDays} upToKey={todayKey} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <StreakBar label="Full Days"            value={thisWk.fullDays}      total={thisWeekDays.filter(dk => dk <= todayKey).length} color="#2563eb" />
                <StreakBar label={catLabels.nutrition}  value={thisWk.nutritionDays} total={thisWeekDays.filter(dk => dk <= todayKey).length} color="#ef4444" />
                <StreakBar label={catLabels.movement}   value={thisWk.movementDays}  total={thisWeekDays.filter(dk => dk <= todayKey).length} color="#22c55e" />
                <StreakBar label={catLabels.mindset}    value={thisWk.mindsetDays}   total={thisWeekDays.filter(dk => dk <= todayKey).length} color="#a855f7" />
              </div>
              {thisWk.completedSideQuests.length > 0 && (
                <div style={{ marginTop: "12px", color: "#7c3aed", fontWeight: "bold", fontSize: "13px" }}>
                  ✨ {thisWk.completedSideQuests.length} side quest{thisWk.completedSideQuests.length !== 1 ? "s" : ""} completed
                </div>
              )}
              {thisWk.weeklyChallengeDays > 0 && (
                <div style={{ marginTop: "8px", color: "#854d0e", fontWeight: "bold", fontSize: "13px" }}>
                  ⭐ Weekly challenge: {thisWk.weeklyChallengeDays} day{thisWk.weeklyChallengeDays !== 1 ? "s" : ""} so far
                </div>
              )}
            </div>

          </div>
        );
      })()}

      {/* ════════════════════ XP LOG TAB ════════════════════ */}
      {subTab === "xplog" && profileData && (() => {
        // Build a timeline of XP events from dailySelections + sideQuestCompletions (date-keyed)
        const computedEvents = [];

        // Habits
        Object.entries(dailySelections || {}).forEach(([dateKey, sel]) => {
          if (!sel) return;
          const catLabels = { nutrition: "Nutrition", movement: "Movement", mindset: "Mindset", ...(categoryLabels || {}) };
          const cats = [
            { key: "nutrition", emoji: "🍓", label: catLabels.nutrition },
            { key: "movement", emoji: "🚶‍♀️", label: catLabels.movement },
            { key: "mindset", emoji: "🌙", label: catLabels.mindset },
          ];
          cats.forEach(({ key, emoji, label }) => {
            if (!sel[key]) return;
            const habit = (customHabits || {})[key]?.[sel[key]];
            if (!habit) return;
            computedEvents.push({ dateKey, emoji, label: `${label} · ${habit.habitName}`, xp: habit.xp, positive: true });
          });
          if (sel.weekly) {
            const weeklyChallenge = (gameContent?.weeklyChallenges || []).find((c) => {
              if (!c.active) return false;
              if (c.startDate && c.endDate) return dateKey >= c.startDate && dateKey <= c.endDate;
              return false;
            }) || (gameContent?.weeklyChallenges || []).filter((c) => c.active && (!c.startDate || !c.endDate))[0];
            computedEvents.push({
              dateKey, emoji: "⭐",
              label: `Weekly Challenge${weeklyChallenge?.text ? ` · ${weeklyChallenge.text}` : ""}`,
              xp: weeklyChallenge?.xp || 10, positive: true,
            });
          }
        });

        // Side quests
        Object.entries(sideQuestCompletions || {}).forEach(([dateKey, quests]) => {
          Object.entries(quests || {}).forEach(([title, done]) => {
            if (!done) return;
            const quest = (activeSideQuests || []).find((q) => q.title === title);
            computedEvents.push({ dateKey, emoji: quest?.emoji || "✨", label: title, xp: quest?.xp || 0, positive: true });
          });
        });

        // Firestore xpAdjustments entries (broadcasts, assignments, body metrics, revives, manual adjustments)
        const sourceEmojiMap = {
          broadcast: "📢", assignment: "📝", body_metric: "📊", revive: "💖", token: "🪙", challenge: "🏆",
        };
        const firestoreEvents = (xpAdjustments || []).map((entry) => {
          const ts = entry.createdAt?.toDate?.();
          const dateKey = ts ? ts.toISOString().slice(0, 10) : null;
          if (!dateKey) return null;
          const positive = entry.type === "earn" || entry.type === "add";
          return {
            dateKey,
            emoji: sourceEmojiMap[entry.source] || (positive ? "💎" : "💸"),
            label: entry.note || entry.source || (positive ? "XP earned" : "XP spent"),
            xp: entry.amount || 0,
            positive,
            sortTs: ts?.getTime() || 0,
          };
        }).filter(Boolean);

        // Merge: computed events get a sortTs of noon on their dateKey so they sort stably
        const allEvents = [
          ...computedEvents.map((e) => ({ ...e, sortTs: new Date(`${e.dateKey}T12:00:00`).getTime() })),
          ...firestoreEvents,
        ].sort((a, b) => b.sortTs - a.sortTs);

        // Group by date
        const grouped = {};
        allEvents.forEach((e) => {
          if (!grouped[e.dateKey]) grouped[e.dateKey] = [];
          grouped[e.dateKey].push(e);
        });
        const dates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

        if (xpAdjustmentsLoading) {
          return <div style={{ ...card, color: "#6b8cb0", textAlign: "center" }}>Loading XP history...</div>;
        }

        return (
          <div>
            <div style={{ ...card, display: "flex", gap: "20px", flexWrap: "wrap" }}>
              <div style={{ textAlign: "center", flex: 1 }}>
                <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1e3a5f" }}>{profileData.totalXP}</div>
                <div style={{ color: "#6b8cb0", fontSize: "12px" }}>Total XP</div>
              </div>
              <div style={{ textAlign: "center", flex: 1 }}>
                <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1e3a5f" }}>{allEvents.length}</div>
                <div style={{ color: "#6b8cb0", fontSize: "12px" }}>Activities logged</div>
              </div>
              <div style={{ textAlign: "center", flex: 1 }}>
                <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1e3a5f" }}>{dates.length}</div>
                <div style={{ color: "#6b8cb0", fontSize: "12px" }}>Active days</div>
              </div>
            </div>

            {dates.length === 0 ? (
              <div style={{ ...card, color: "#6b8cb0", textAlign: "center" }}>
                No XP history yet — complete habits to see your history here.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {dates.map((dateKey) => {
                  const dayEvents = grouped[dateKey];
                  const dayNet = dayEvents.reduce((s, e) => s + (e.positive ? e.xp : -e.xp), 0);
                  return (
                    <div key={dateKey} style={{ ...card, marginBottom: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "14px" }}>{formatDate(dateKey)}</div>
                        <div style={{ background: dayNet >= 0 ? "#e0f2fe" : "#fff1f2", borderRadius: "999px", padding: "3px 10px", color: dayNet >= 0 ? "#1e3a5f" : "#dc2626", fontWeight: "bold", fontSize: "12px" }}>
                          {dayNet >= 0 ? "+" : ""}{dayNet} XP
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {dayEvents.map((e, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 10px", borderRadius: "10px", background: e.positive ? "#f8fbff" : "#fff8f8", border: e.positive ? "1px solid #dbeafe" : "1px solid #fecaca" }}>
                            <span style={{ fontSize: "18px", flexShrink: 0 }}>{e.emoji}</span>
                            <span style={{ flex: 1, color: "#1e3a5f", fontSize: "13px" }}>{e.label}</span>
                            {e.xp > 0 && (
                              <span style={{ color: e.positive ? "#2563eb" : "#dc2626", fontWeight: "bold", fontSize: "12px", flexShrink: 0 }}>
                                {e.positive ? "+" : "-"}{e.xp}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ════════════════════ PHOTOS TAB ════════════════════ */}
      {subTab === "photos" && (
        <>
          {/* Upload */}
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
              <span style={sectionTitle}>Add Photos</span>
              {isMetricDueToday("photos") && <span style={{ background: "#fef9c3", color: "#92400e", fontSize: "11px", fontWeight: "bold", padding: "2px 7px", borderRadius: "999px", border: "1px solid #fde68a" }}>Due today</span>}
              {getMetricXpForDate("photos", photoDate) > 0 && <span style={{ background: "#dcfce7", color: "#166534", fontSize: "11px", fontWeight: "bold", padding: "2px 7px", borderRadius: "999px", border: "1px solid #bbf7d0" }}>+{getMetricXpForDate("photos", photoDate)} XP</span>}
            </div>
            <div style={{ color: "#6b8cb0", fontSize: "13px", marginBottom: "14px" }}>
              Front, side, and back for a date.
            </div>

            <div style={{ marginBottom: "14px" }}>
              <div style={labelStyle}>Date</div>
              <input type="date" value={photoDate}
                onChange={(e) => setPhotoDate(e.target.value)}
                style={{ ...inputStyle, maxWidth: "190px" }} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
              {ANGLES.map((angle) => {
                const existing = currentDatePhotos[angle];
                const isUploading = uploading[angle];
                return (
                  <div key={angle} style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "8px", textTransform: "capitalize" }}>
                      {angle}
                    </div>

                    {existing ? (
                      <div style={{ position: "relative" }}>
                        <img src={existing} alt={angle} style={{
                          width: "100%", aspectRatio: "3/4", objectFit: "cover",
                          borderRadius: "14px", border: "2px solid #bfdbfe",
                        }} />
                        {deleteConfirm?.date === photoDate && deleteConfirm?.angle === angle ? (
                          <div style={{
                            position: "absolute", inset: 0, borderRadius: "14px",
                            background: "rgba(0,0,0,0.55)", display: "flex",
                            flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px",
                          }}>
                            <div style={{ color: "#fff", fontWeight: "bold", fontSize: "12px" }}>Delete?</div>
                            <div style={{ display: "flex", gap: "6px" }}>
                              <button
                                onClick={() => { handleDeletePhoto(photoDate, angle); setDeleteConfirm(null); }}
                                style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
                              >Yes</button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#fff", color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
                              >No</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirm({ date: photoDate, angle })}
                            style={{
                              position: "absolute", top: "6px", right: "6px",
                              width: "24px", height: "24px", borderRadius: "999px",
                              border: "none", background: "rgba(255,255,255,0.9)",
                              color: "#1e3a5f", cursor: "pointer", fontSize: "12px",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                            ✕
                          </button>
                        )}
                      </div>
                    ) : (
                      <button onClick={() => fileRefs[angle].current?.click()}
                        disabled={isUploading}
                        style={{
                          width: "100%", aspectRatio: "3/4", borderRadius: "14px",
                          border: "2px dashed #93c5fd", background: "#f8fbff",
                          color: "#6b8cb0", cursor: isUploading ? "default" : "pointer",
                          fontSize: "13px", display: "flex", flexDirection: "column",
                          alignItems: "center", justifyContent: "center", gap: "6px",
                        }}>
                        {isUploading ? "Uploading..." : <><span style={{ fontSize: "22px" }}>+</span><span>Add</span></>}
                      </button>
                    )}

                    <input ref={fileRefs[angle]} type="file" accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handlePhotoUpload(angle, file);
                      }} />
                  </div>
                );
              })}
            </div>

            {photoError && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "8px 12px", color: "#dc2626", fontSize: "13px", marginTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {photoError}
                <button onClick={() => setPhotoError("")} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: "16px", lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
            )}
          </div>

          {/* Compare */}
          {photoDates.length >= 2 && (
            <div style={card}>
              <div style={{ ...sectionTitle, marginBottom: "4px" }}>Compare</div>
              <div style={{ color: "#6b8cb0", fontSize: "13px", marginBottom: "14px" }}>
                Pick two dates and an angle to compare side by side.
              </div>

              <div style={{ display: "flex", gap: "8px", marginBottom: "12px", flexWrap: "wrap" }}>
                {[["Date A", compareA, setCompareA], ["Date B", compareB, setCompareB]].map(([label, val, setter]) => (
                  <div key={label} style={{ flex: 1, minWidth: "120px" }}>
                    <div style={labelStyle}>{label}</div>
                    <select value={val} onChange={(e) => setter(e.target.value)} style={inputStyle}>
                      <option value="">Select</option>
                      {photoDates.map((d) => <option key={d} value={d}>{formatDate(d)}</option>)}
                    </select>
                  </div>
                ))}
                <div style={{ flex: 1, minWidth: "100px" }}>
                  <div style={labelStyle}>Angle</div>
                  <select value={compareAngle} onChange={(e) => setCompareAngle(e.target.value)} style={inputStyle}>
                    {ANGLES.map((a) => <option key={a} value={a} style={{ textTransform: "capitalize" }}>{a}</option>)}
                  </select>
                </div>
              </div>

              {compareA && compareB && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  {[[compareA, photoByDate[compareA]], [compareB, photoByDate[compareB]]].map(([date, data]) => (
                    <div key={date} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "12px", color: "#6b8cb0", marginBottom: "6px" }}>{formatDate(date)}</div>
                      {data?.[compareAngle] ? (
                        <img src={data[compareAngle]} alt={`${compareAngle} ${date}`}
                          style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", borderRadius: "14px", border: "2px solid #bfdbfe" }} />
                      ) : (
                        <div style={{
                          width: "100%", aspectRatio: "3/4", borderRadius: "14px",
                          border: "2px dashed #93c5fd", background: "#f8fbff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#6b8cb0", fontSize: "13px",
                        }}>
                          No {compareAngle} photo
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History */}
          {photoDates.length > 0 && (
            <div style={card}>
              <div style={{ ...sectionTitle, marginBottom: "14px" }}>History</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {photoDates.map((date) => {
                  const entry = photoByDate[date];
                  return (
                    <div key={date}>
                      <div style={{ fontSize: "13px", fontWeight: "bold", color: "#1e3a5f", marginBottom: "8px" }}>
                        {formatDate(date)}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                        {ANGLES.map((angle) =>
                          entry[angle] ? (
                            <div key={angle} style={{ position: "relative" }}>
                              <img src={entry[angle]} alt={angle}
                                style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", borderRadius: "12px", border: "2px solid #bfdbfe" }} />
                              {deleteConfirm?.date === date && deleteConfirm?.angle === angle ? (
                                <div style={{
                                  position: "absolute", inset: 0, borderRadius: "12px",
                                  background: "rgba(0,0,0,0.55)", display: "flex",
                                  flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px",
                                }}>
                                  <div style={{ color: "#fff", fontWeight: "bold", fontSize: "12px" }}>Delete?</div>
                                  <div style={{ display: "flex", gap: "6px" }}>
                                    <button
                                      onClick={() => { handleDeletePhoto(date, angle); setDeleteConfirm(null); }}
                                      style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#ef4444", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
                                    >Yes</button>
                                    <button
                                      onClick={() => setDeleteConfirm(null)}
                                      style={{ padding: "5px 12px", borderRadius: "8px", border: "none", background: "#fff", color: "#1e3a5f", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}
                                    >No</button>
                                  </div>
                                </div>
                              ) : (
                                <button onClick={() => setDeleteConfirm({ date, angle })}
                                  style={{
                                    position: "absolute", top: "6px", right: "6px",
                                    width: "24px", height: "24px", borderRadius: "999px",
                                    border: "none", background: "rgba(255,255,255,0.9)",
                                    color: "#1e3a5f", cursor: "pointer", fontSize: "12px",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                  }}>✕</button>
                              )}
                            </div>
                          ) : (
                            <div key={angle} style={{
                              width: "100%", aspectRatio: "3/4", borderRadius: "12px",
                              border: "1px dashed #e0d4ef", background: "#f5faff",
                            }} />
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {photoDates.length === 0 && (
            <div style={{ color: "#6b8cb0", fontSize: "13px", textAlign: "center", marginTop: "20px" }}>
              No photos yet. Add your first set above.
            </div>
          )}
        </>
      )}
    </div>
  );
}
